// Copyright Prashant Vaghela, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { FC, useState } from 'react';
import React from 'react';
import { Handle, NodeProps, Position } from 'reactflow';

import {
  Box,
  Button,
  Cards,
  ColumnLayout,
  Container,
  Header,
  ProgressBar,
  ProgressBarProps,
  SpaceBetween,
  StatusIndicator,
  Tabs,
  TabsProps,
  TextContent,
} from '@cloudscape-design/components';

// --- Type Definitions for CustomNode ---
export interface CustomNodeData {
  id: string;
  label?: string;
  line?: number;
  sequenceNumber?: number;
  duration?: number | string;
  operationType?: string;
  oee?: number | string;
  status?: boolean;
  direction?: 'LR' | 'TB';
}

const CustomNode: FC<NodeProps<CustomNodeData>> = ({ data }) => {
  const getColor = (): string | 'success' | 'warning' => {
    const id = data.id || '';
    const oee = parseFloat(data.oee as string) || 0; // Type assertion since data.oee could be string
    if (oee >= 80) {
      return 'success';
    }
    if (oee >= 60) {
      return 'warning';
    }
    if (id.includes('MTB')) {
      return '#FFFFE0';
    }
    if (id.includes('Road')) {
      return '#FFC0CB';
    }
    if (id.includes('Tour')) {
      return '#FFDAB9';
    }
    if (id.includes('Cross')) {
      return '#D3D3D3';
    }
    return '#90EE90';
  };

  const getStatus = (status: boolean | undefined): 'success' | 'error' => {
    return status ? 'success' : 'error';
  };

  return (
    <Container
      header={
        <Header variant="h3" description={`Sequence: ${data.sequenceNumber || 'N/A'}`}>
          {data.label || 'No Label'}
        </Header>
      }
    >
      <Handle
        type="target"
        position={data?.direction === 'LR' ? Position.Left : Position.Top}
        style={{ background: '#555' }}
      />
      <ColumnLayout columns={2} variant="text-grid">
        <Box variant="awsui-key-label">Duration | {data.duration || 'N/A'}</Box>
        <Box variant="awsui-key-label">Type | {data.operationType || 'N/A'}</Box>
        <Box variant="awsui-key-label">OEE | {data.oee || 'N/A'}</Box>
        <Box variant="awsui-key-label">
          Status |{' '}
          <StatusIndicator type={getStatus(data.status)}>{data.status ? 'Active' : 'Inactive'}</StatusIndicator>
        </Box>
      </ColumnLayout>
      <Box padding={{ top: 's' }}>
        <ProgressBar
          value={parseFloat(data.duration as string) || 0}
          status={getColor() as ProgressBarProps.Status}
          resultText={`${data.duration || 0}%`}
          additionalInfo="Duration progress"
        />
      </Box>
      <Handle
        type="source"
        position={data?.direction === 'LR' ? Position.Right : Position.Bottom}
        style={{ background: '#555' }}
      />
    </Container>
  );
};

export default CustomNode;

// --- Type Definitions for MPCPCustomNode ---
type ButtonType = 'P' | 'Q' | 'C' | 'D' | 'S' | 'M' | 'E';

interface MetricData {
  goal: number;
  actual: number;
}

export interface MPCPCustomNodeItem {
  id: string;
  name: string;
  description: string;
  line?: number;
  sequenceNumber?: number;
  onButtonTypeSelect: (type: ButtonType | null, data: MPCPCustomNodeItem) => void;
  // Dynamic properties for each button type
  P: MetricData[];
  Q: MetricData[];
  C: MetricData[];
  D: MetricData[];
  S: MetricData[];
  M: MetricData[];
  E: MetricData[];
}

interface MPCPCustomNodeProps {
  data: MPCPCustomNodeItem;
}

export const MPCPCustomNode: FC<MPCPCustomNodeProps> = ({ data }) => {
  const handleTypeSelect = (type: ButtonType) => {
    data.onButtonTypeSelect(type, data);
  };

  const buttonTypes: ButtonType[] = ['P', 'Q', 'C', 'D', 'S', 'M', 'E'];

  // The type assertion `as string` is used here because the keys in `data` are dynamically accessed.
  // The structure `data[type][0].actual` is guaranteed by the interface MPCPCustomNodeItem.
  const average =
    buttonTypes.reduce((acc, type) => acc + parseFloat(data[type][0].actual as unknown as string), 0) /
    buttonTypes.length;

  return (
    <Cards
      cardDefinition={{
        header: item => (
          <Header variant="h2" description={item.name}>
            {item.description || 'Designation'}
          </Header>
        ),
        sections: [
          {
            id: 'buttons',
            content: () => (
              <SpaceBetween size="s" direction="horizontal">
                {buttonTypes.map(type => {
                  return (
                    <Button key={type} variant="primary" onClick={() => handleTypeSelect(type)}>
                      {type}
                    </Button>
                  );
                })}
              </SpaceBetween>
            ),
          },
          {
            id: 'progress',
            content: () => (
              <ProgressBar value={Math.round(average)} label="Progress" description={`${Math.round(average)}%`} />
            ),
          },
        ],
      }}
      items={[data]}
      trackBy="id"
      empty={
        <Box textAlign="center" color="inherit">
          <b>No data</b>
          <Box padding={{ bottom: 's' }} variant="p" color="inherit">
            No data to display.
          </Box>
        </Box>
      }
    />
  );
};

// --- Type Definitions for MPCustomNode ---
export interface MPCustomNodeData {
  id: string;
  type: string;
  label?: string;
  direction?: 'LR' | 'TB';
  sequenceNumber?: number;
  UOM?: string;
  Freq?: string;
  Actual?: string;
  Goal?: string;
  responsibility?: string[];
  skill?: string[];
  attitude?: string[];
  line?: number;
}

const extractNumbers = (value: string | undefined): number => {
  if (typeof value === 'string') {
    // Only allow digits and decimal point, then parse
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
    return isNaN(numericValue) ? 0 : numericValue;
  }
  return 0;
};

export const MPCustomNode: FC<NodeProps<MPCustomNodeData>> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const hasSource = data.id !== data.type; // Check if it's a "detail" node

  const actualValue = extractNumbers(data?.Actual);
  const goalValue = extractNumbers(data?.Goal);

  const percent = goalValue > 0 ? Math.round((actualValue / goalValue) * 100) : 0;

  const tabsItems: TabsProps.Tab[] = [
    {
      label: 'Responsibility',
      id: 'R',
      content: data?.responsibility && (
        <SpaceBetween size="s">
          <TextContent>
            <h4>Responsibility:</h4>
            <ol>
              {data.responsibility.map(
                (
                  item:
                    | string
                    | number
                    | boolean
                    | React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>
                    | React.ReactFragment
                    | React.ReactPortal
                    | null
                    | undefined,
                  index: React.Key | null | undefined,
                ) => (
                  <li key={index}>{item}</li>
                ),
              )}
            </ol>
          </TextContent>
        </SpaceBetween>
      ),
    },
    {
      label: 'Skill/Knowledge',
      id: 'S',
      content: data?.skill && (
        <SpaceBetween size="s">
          <TextContent>
            <h4>Skill/Knowledge:</h4>
            <ol>
              {data.skill.map(
                (
                  item:
                    | string
                    | number
                    | boolean
                    | React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>
                    | React.ReactFragment
                    | React.ReactPortal
                    | null
                    | undefined,
                  index: React.Key | null | undefined,
                ) => (
                  <li key={index}>{item}</li>
                ),
              )}
            </ol>
          </TextContent>
        </SpaceBetween>
      ),
    },
    {
      label: 'Attitude Towards',
      id: 'A',
      content: data?.attitude && (
        <SpaceBetween size="s">
          <TextContent>
            <h4>Attitude Towards:</h4>
            <ol>
              {data.attitude.map(
                (
                  item:
                    | string
                    | number
                    | boolean
                    | React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>
                    | React.ReactFragment
                    | React.ReactPortal
                    | null
                    | undefined,
                  index: React.Key | null | undefined,
                ) => (
                  <li key={index}>{item}</li>
                ),
              )}
            </ol>
          </TextContent>
        </SpaceBetween>
      ),
    },
  ].filter(tab => tab.content !== null) as TabsProps.Tab[]; // Filter out null content

  return (
    <Container header={<Header>{data.label || 'No Label'}</Header>}>
      <Handle
        type="target"
        position={data.direction === 'LR' ? Position.Left : Position.Top}
        style={{ background: '#555', width: '12px', height: '12px' }}
      />
      {hasSource && (
        <SpaceBetween size="s">
          <ColumnLayout columns={2} variant="text-grid">
            <TextContent>
              <p>
                <strong>UOM:</strong> {data.UOM || 'N/A'}
              </p>
            </TextContent>
            <TextContent>
              <p>
                <strong>Freq:</strong> {data.Freq || 'N/A'}
              </p>
            </TextContent>
            <TextContent>
              <p>
                <strong>Actual:</strong> {data.Actual || 'N/A'}
              </p>
            </TextContent>
            <TextContent>
              <p>
                <strong>Goal:</strong> {data.Goal || 'N/A'}
              </p>
            </TextContent>
          </ColumnLayout>
          <ProgressBar value={percent} label="Progress" description={`${percent}%`} />
          <Button onClick={toggleExpand}>{expanded ? 'Hide Checking Point' : 'Show Checking Point'}</Button>
          {expanded && tabsItems.length > 0 && <Tabs tabs={tabsItems} />}
        </SpaceBetween>
      )}
      <Handle
        type="source"
        position={data.direction === 'LR' ? Position.Right : Position.Bottom}
        style={{ background: '#555', width: '12px', height: '12px' }}
      />
    </Container>
  );
};
